import { QUEST_APP, QUEST_TYPE, TMA_QUEST_TYPES } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface UseQuestDataProps {
  enabled?: boolean;
}

/**
 * Hook to fetch all quest-related data from the API
 * Returns raw data from the 4 quest endpoints
 */
export const useQuestData = ({ enabled = true }: UseQuestDataProps = {}) => {
  const { hubUser, auth } = useHubUser();
  const apiHelper = useApi();
  const { isTMA } = useTelegramContext();
  const queryClient = useQueryClient();

  let questTypes = `${QUEST_TYPE.TRADING_VOLUME},${QUEST_TYPE.SOCIAL_CONNECTION},${QUEST_TYPE.CEX_CONNECTION},${QUEST_TYPE.USER_AFFILIATED},${QUEST_TYPE.TWITTER_FOLLOW_ACCOUNT},${QUEST_TYPE.TWITTER_LIKE_POST},${QUEST_TYPE.TWITTER_RETWEET_POST},${QUEST_TYPE.TWITTER_COMMENT_POST},${QUEST_TYPE.TWITTER_SEND_POST},${QUEST_TYPE.KYC_AND_TRADING}`;
  if (isTMA) questTypes += `,${TMA_QUEST_TYPES.join(",")}`;

  // 1. Get all live quests
  const { data: liveQuests, isLoading: isLoadingLiveQuests } = useQuery({
    queryKey: [QueryKeys.QUEST_LIVE, hubUser?.id],
    queryFn: () =>
      apiHelper.getWithAuth(auth, API_ENDPOINT.QUEST_LIVE, {
        queryParams: { types: questTypes, app: QUEST_APP.HUB },
      }),
    enabled: !!hubUser?.id && enabled,
    staleTime: 30_000,
  });

  // 2. Get all user quest progress
  const { data: userQuestProgress, isLoading: isLoadingUserQuestProgress } = useQuery({
    queryKey: [QueryKeys.QUEST_USER_PROGRESS, hubUser?.id],
    queryFn: () =>
      apiHelper.getWithAuth(auth, API_ENDPOINT.QUEST_USER_PROGRESS, {
        queryParams: { types: questTypes, app: QUEST_APP.HUB },
      }),
    enabled: !!hubUser?.id && enabled,
    staleTime: 30_000,
  });

  // 3. Get all user quest completions
  const { data: userQuestCompletions, isLoading: isLoadingUserQuestCompletions } = useQuery({
    queryKey: [QueryKeys.QUEST_USER_COMPLETIONS, hubUser?.id],
    queryFn: () =>
      apiHelper.getWithAuth(auth, API_ENDPOINT.QUEST_USER_COMPLETIONS, {
        queryParams: { types: questTypes, app: QUEST_APP.HUB },
      }),
    enabled: !!hubUser?.id && enabled,
    staleTime: 30_000,
  });

  // 4. Get all user quest eligibility
  const { data: userQuestEligibility, isLoading: isLoadingUserQuestEligibility } = useQuery({
    queryKey: [QueryKeys.QUEST_USER_ELIGIBILITY, hubUser?.id],
    queryFn: () =>
      apiHelper.getWithAuth(auth, API_ENDPOINT.QUEST_USER_ELIGIBILITY, {
        queryParams: { types: questTypes, app: QUEST_APP.HUB },
      }),
    enabled: !!hubUser?.id && enabled,
    staleTime: 30_000,
  });

  const isLoading =
    isLoadingLiveQuests ||
    isLoadingUserQuestProgress ||
    isLoadingUserQuestCompletions ||
    isLoadingUserQuestEligibility ||
    !hubUser?.id; // Stay loading until hubUser is available

  const invalidQuestsStatus = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [QueryKeys.QUEST_USER_PROGRESS] }),
      queryClient.invalidateQueries({ queryKey: [QueryKeys.QUEST_USER_COMPLETIONS] }),
      queryClient.invalidateQueries({ queryKey: [QueryKeys.QUEST_USER_ELIGIBILITY] }),
    ]);
  };

  return {
    liveQuests,
    userQuestProgress,
    userQuestCompletions,
    userQuestEligibility,
    isLoading,
    invalidQuestsStatus,
  };
};
